import React from 'react'
import clsx from 'clsx'
import {
  CheckIcon,
  ExclamationTriangleIcon,
  ArrowUpIcon,
  ArrowDownIcon
} from '@heroicons/react/20/solid'
import { IssuesPanel } from '../../../../Elements/JirraIssue'
import type { UserAnalysis, Issue, AddedByResponse, Category } from '../types'

const isBug = (i: Issue | unknown): i is Issue =>
  (i as Issue)?.type === 'Bug'

export const CommitmentBar: React.FC<{
  userAnalysis: UserAnalysis
  sprintId: string
  openPanel: null | 'bugs' | 'tickets' | 'changes'
  setOpenPanel: (panel: null | 'bugs' | 'tickets' | 'changes') => void
  addedByLookup: Record<string, AddedByResponse>
  isLoadingAddedBy: boolean
  bugCategories: Category[]
  ticketCategories: Category[]
  changeCategories: Category[]
}> = ({ userAnalysis, sprintId, openPanel, setOpenPanel, addedByLookup, isLoadingAddedBy, bugCategories, ticketCategories, changeCategories }) => {
  const pct = userAnalysis.resolvedPoints / userAnalysis.startPoints
  const colorClass = pct > 0.85 ? 'text-green-600' : pct >= 0.5 ? 'text-orange-500' : 'text-red-600'
  const barClass = pct > 0.99 ? 'bg-green-500' : pct > 0.85 ? 'bg-yellow-500' : pct > 0.5 ? 'bg-orange-500' : 'bg-red-500'

  const c = userAnalysis.changes
  const bugsAdded = c?.bugsAdded ?? 0
  const bugsRemoved = c?.bugsRemoved ?? 0
  const bugsNet = bugsAdded - bugsRemoved
  const ticketsAdded = c?.addedIssues.filter(i => !isBug(i)).length ?? 0
  const ticketsRemoved = c?.removedIssues.filter(i => !isBug(i)).length ?? 0
  const ticketsNet = ticketsAdded - ticketsRemoved
  const pointsNet = (c?.pointsAdded ?? 0) - (c?.pointsRemoved ?? 0)
  const estimatesChanged = c?.changedIssues.length ?? 0
  const pointsChanged = c?.pointsChanged ?? 0
  const hasBugChanges = bugsAdded !== 0 || bugsRemoved !== 0
  const hasTicketChanges = ticketsAdded !== 0 || ticketsRemoved !== 0
  const hasEstimateChanges = estimatesChanged !== 0

  return (
    <>
      <div className='mb-4 rounded-lg border border-gray-200 bg-white px-6 py-4 shadow-sm'>
        <div className='flex items-center justify-between'>
          <div className='flex items-center'>
            <div className='flex h-12 w-12 items-center justify-center rounded-full bg-indigo-100'>
              <span className='text-xl font-bold text-indigo-600'>{userAnalysis.startPoints}</span>
            </div>
            <div className='ml-3'>
              <p className='text-sm font-medium text-gray-900'>Committed</p>
              <p className='text-xs text-gray-500'>Start of sprint</p>
              <p className='mt-0.5 text-xs text-gray-400'>{userAnalysis.startBugs} bug{userAnalysis.startBugs !== 1 ? 's' : ''}</p>
            </div>
          </div>

          <div className='mx-6 flex-1'>
            <div className={clsx('mb-1 flex items-center justify-center gap-1', colorClass)}>
              {pct >= 0.85 ? (
                <CheckIcon className='h-4 w-4' />
              ) : pct < 0.5 ? (
                <ExclamationTriangleIcon className='h-4 w-4' />
              ) : null}
              <span className='text-lg font-bold'>
                {Number(pct).toLocaleString(undefined, { style: 'percent', minimumFractionDigits: 0 })}
              </span>
              {(() => {
                const diff = userAnalysis.resolvedPoints - userAnalysis.startPoints
                if (diff === 0) return null
                const abs = Math.abs(diff)
                const label = diff < 0
                  ? `${abs} point${abs !== 1 ? 's' : ''} under commitment`
                  : `${abs} point${abs !== 1 ? 's' : ''} over commitment`
                return (
                  <span className='text-sm font-normal text-gray-500'>({label})</span>
                )
              })()}
            </div>
            <div className='relative h-2.5 rounded-full bg-gray-200'>
              <div
                className={clsx('absolute h-2.5 rounded-full transition-all', barClass)}
                style={{ width: `${Math.min(100, pct * 100)}%` }}
              />
            </div>
            <div className='mt-3 grid grid-cols-3 gap-2'>
              <div className='group relative'>
                <button
                  disabled={!hasBugChanges}
                  onClick={hasBugChanges ? () => setOpenPanel('bugs') : undefined}
                  className={clsx(
                    'w-full rounded-md px-2 py-1.5 text-center transition-colors',
                    hasBugChanges ? 'cursor-pointer bg-red-50 hover:bg-red-100' : 'cursor-default bg-gray-50'
                  )}
                >
                  <p className={clsx('text-sm font-bold', hasBugChanges ? 'text-red-600' : 'text-gray-400')}>
                    {bugsNet >= 0 ? '+' : ''}{bugsNet} Bugs
                  </p>
                  <p className={clsx('text-xs', hasBugChanges ? 'text-red-400' : 'text-gray-400')}>
                    {bugsAdded} Added, {bugsRemoved} Removed
                  </p>
                </button>
                <div className='pointer-events-none absolute bottom-full left-0 z-10 mb-2 w-64 rounded-lg bg-indigo-600 p-3 text-xs text-white opacity-0 shadow-lg transition-opacity group-hover:opacity-100'>
                  <p className='mb-1.5 font-semibold'>How bugs are counted</p>
                  <p className='mb-1 text-gray-300'>Added — bug tickets that were not in this user&apos;s sprint at the start but are present at the end.</p>
                  <p className='text-gray-300'>Removed — bug tickets that were in this user&apos;s sprint at the start but are no longer present at the end.</p>
                  <p className='mt-1.5 text-gray-400'>Ownership corrections (reassignments) are excluded — only genuine scope changes count.</p>
                </div>
              </div>
              <div className='group relative'>
                <button
                  disabled={!hasTicketChanges}
                  onClick={hasTicketChanges ? () => setOpenPanel('tickets') : undefined}
                  className={clsx(
                    'w-full rounded-md px-2 py-1.5 text-center transition-colors',
                    hasTicketChanges ? 'cursor-pointer bg-sky-50 hover:bg-sky-100' : 'cursor-default bg-gray-50'
                  )}
                >
                  <p className={clsx('text-sm font-bold', hasTicketChanges ? 'text-sky-600' : 'text-gray-400')}>
                    {ticketsNet >= 0 ? '+' : ''}{ticketsNet} Tickets ({pointsNet >= 0 ? '+' : ''}{pointsNet} pts)
                  </p>
                  <p className={clsx('text-xs', hasTicketChanges ? 'text-sky-400' : 'text-gray-400')}>
                    {ticketsAdded} Added, {ticketsRemoved} Removed
                  </p>
                </button>
                <div className='pointer-events-none absolute bottom-full left-0 z-10 mb-2 w-64 rounded-lg bg-indigo-600 p-3 text-xs text-white opacity-0 shadow-lg transition-opacity group-hover:opacity-100'>
                  <p className='mb-1.5 font-semibold'>How tickets are counted</p>
                  <p className='mb-1 text-gray-300'>Added — non-bug tickets injected into this user&apos;s sprint after it started.</p>
                  <p className='text-gray-300'>Removed — non-bug tickets that were committed at sprint start but are no longer present at the end.</p>
                  <p className='mt-1.5 text-gray-400'>Ownership corrections (reassignments) are excluded — only genuine scope changes count.</p>
                </div>
              </div>
              <div className='group relative'>
                <button
                  disabled={!hasEstimateChanges}
                  onClick={hasEstimateChanges ? () => setOpenPanel('changes') : undefined}
                  className={clsx(
                    'w-full rounded-md px-2 py-1.5 text-center transition-colors',
                    hasEstimateChanges ? 'cursor-pointer bg-orange-50 hover:bg-orange-100' : 'cursor-default bg-gray-50'
                  )}
                >
                  <p className={clsx('text-sm font-bold', hasEstimateChanges ? 'text-orange-600' : 'text-gray-400')}>
                    {estimatesChanged} Changes ({pointsChanged >= 0 ? '+' : ''}{pointsChanged} pts)
                  </p>
                  <p className={clsx('text-xs', hasEstimateChanges ? 'text-orange-400' : 'text-gray-400')}>
                    {pointsChanged === 0 ? 'No Change in Estimates' : `${pointsChanged >= 0 ? '+' : ''}${pointsChanged} in Estimates`}
                  </p>
                </button>
                <div className='pointer-events-none absolute bottom-full left-0 z-10 mb-2 w-64 rounded-lg bg-indigo-600 p-3 text-xs text-white opacity-0 shadow-lg transition-opacity group-hover:opacity-100'>
                  <p className='mb-1.5 font-semibold'>How estimate changes are counted</p>
                  <p className='mb-1 text-gray-300'>Counts tickets whose story point estimate changed during the sprint.</p>
                  <p className='text-gray-300'>Points delta shows the net change in total committed points from those re-estimates.</p>
                </div>
              </div>
            </div>
          </div>

          <div className='flex items-center'>
            <div className='mr-3 text-right'>
              <p className='text-sm font-medium text-gray-900'>Delivered</p>
              <p className='text-xs text-gray-500'>End of sprint</p>
              <p className='mt-0.5 flex items-center justify-end gap-0.5 text-xs'>
                {userAnalysis.resolvedBugs > userAnalysis.startBugs ? (
                  <ArrowUpIcon className='h-3 w-3 text-green-500' />
                ) : userAnalysis.resolvedBugs < userAnalysis.startBugs ? (
                  <ArrowDownIcon className='h-3 w-3 text-red-500' />
                ) : (
                  <CheckIcon className='h-3 w-3 text-green-500' />
                )}
                <span className={userAnalysis.resolvedBugs < userAnalysis.startBugs ? 'text-red-500' : 'text-green-500'}>
                  {userAnalysis.resolvedBugs} bug{userAnalysis.resolvedBugs !== 1 ? 's' : ''} resolved
                </span>
              </p>
            </div>
            <div className='flex h-12 w-12 items-center justify-center rounded-full bg-green-100'>
              <span className='text-xl font-bold text-green-600'>{userAnalysis.resolvedPoints}</span>
            </div>
          </div>
        </div>
      </div>

      <IssuesPanel
        title='Bugs'
        headerColor='bg-red-600'
        categories={bugCategories}
        sprintId={sprintId}
        addedByLookup={addedByLookup}
        isLoadingAddedBy={isLoadingAddedBy}
        isOpen={openPanel === 'bugs'}
        setIsOpen={(open) => setOpenPanel(open ? 'bugs' : null)}
      />
      <IssuesPanel
        title='Tickets'
        headerColor='bg-sky-600'
        categories={ticketCategories}
        sprintId={sprintId}
        addedByLookup={addedByLookup}
        isLoadingAddedBy={isLoadingAddedBy}
        isOpen={openPanel === 'tickets'}
        setIsOpen={(open) => setOpenPanel(open ? 'tickets' : null)}
      />
      <IssuesPanel
        title='Estimate Changes'
        headerColor='bg-orange-600'
        categories={changeCategories}
        sprintId={sprintId}
        addedByLookup={addedByLookup}
        isLoadingAddedBy={isLoadingAddedBy}
        isOpen={openPanel === 'changes'}
        setIsOpen={(open) => setOpenPanel(open ? 'changes' : null)}
      />
    </>
  )
}